import React, { useState, useEffect, useCallback } from 'react';
import { Bell, Settings } from 'lucide-react';
import WelcomeCard from './WelcomeCard';
import StatsGrid from './StatsGrid';
import ClassList from './ClassList';
import NotificationList from './NotificationList';
import CreateClassForm from './CreateClassForm';
import TeacherHeader from '../TeacherHeader';
import TeacherFooter from '../TeacherFooter';
import styled from 'styled-components';
import { useAuth } from '../../../contexts/AuthContext';
import ClassDetails from './ClassDetails';
import StudentDetails from './StudentDetails';
import RecentActivities from './RecentActivities';
import PerformanceAnalytics from './PerformanceAnalytics';
import UpcomingEvents from './UpcomingEvents';
import TeacherProfile from './TeacherProfile';
import HelpWidget from './HelpWidget';
import MessagesList from '../../shared/MessagesList';
import axios from 'axios';
import { useWebSocket } from '../../../services/websocketService';

export const DashboardContainer = styled.div`
  min-height: 100vh;
  background-color: #f9f9fb;
  display: flex;
  flex-direction: column;
`;

export const DashboardMain = styled.main`
  flex: 1;
  max-width: 48rem;
  margin: 0 auto;
  width: 100%;
`;

export const DashboardContent = styled.div`
  padding: 1rem;
`;

export const DashboardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

export const DashboardTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
`;

export const ActionButtons = styled.div`
  display: flex;
  gap: 0.75rem;
`;

export const ActionButton = styled.button`
  padding: 0.5rem;
  border-radius: 9999px;
  background-color: #f3f4f6;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.15s ease;
  
  &:hover {
    background-color: #e5e7eb;
  }
  
  svg {
    color: #7e22ce;
  }
`;

const StudentListContainer = styled.div`
  background-color: white;
  border-radius: 0.5rem;
  padding: 1.5rem;
  margin-top: 1.5rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
`;

const StudentListTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 1rem;
`;

const StudentTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.th`
  text-align: left;
  padding: 0.75rem;
  border-bottom: 2px solid #e5e7eb;
  color: #374151;
  font-weight: 500;
`;

const TableCell = styled.td`
  padding: 0.75rem;
  border-bottom: 1px solid #e5e7eb;
  color: #4b5563;
`;

const TableRow = styled.tr`
  &:hover {
    background-color: #f9fafb;
  }
`;

interface Student {
  _id: string;
  firstName: string;
  lastName: string;
  grade: string;
  email: string;
}

interface Class {
  _id: string;
  name: string;
  grade: string;
  students: Student[];
}

interface Notification {
  id: number;
  type: 'alert' | 'info' | 'success';
  message: string;
  time: string;
}

interface ApiResponse {
  success: boolean;
  message: string;
  data: {
    grades: {
      [key: string]: Student[];
    };
  };
}

interface TeacherData {
  firstName: string;
  lastName: string;
  email: string;
  schoolName: string;
  schoolId: string;
}

interface DashboardProps {
  teacherData: TeacherData;
  notifications?: Notification[];
  selectedTerm?: string;
  onSelectClass: (classItem: Class) => void;
}

const Section = styled.section`
  background-color: white;
  border-radius: 0.5rem;
  padding: 1.5rem;
  margin-top: 1.5rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
`;

const SectionTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Dashboard: React.FC<DashboardProps> = ({ teacherData, notifications, selectedTerm, onSelectClass }) => {
  const { user, token } = useAuth();
  const [view, setView] = useState<'dashboard' | 'classDetails' | 'studentDetails'>('dashboard');
  const [selectedClass, setSelectedClass] = useState<Class | null>(null);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [showProfile, setShowProfile] = useState(false);
  const [classSearch, setClassSearch] = useState('');
  const [studentSearch, setStudentSearch] = useState('');
  const [showNotifications, setShowNotifications] = useState(false);
  const [classes, setClasses] = useState<Class[]>([]);
  const [grades, setGrades] = useState<{ [key: string]: Student[] }>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchClassesAndStudents = async () => {
      try {
        setLoading(true);
        setError(null);
        const teacherId = user?.teacherId || user?.id;
        if (!teacherId) {
          setError('Teacher ID not found');
          return;
        }
        const res = await axios.get<ApiResponse>(`/api/teachers/${teacherId}/classes-and-students`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (res.data.success) {
          setGrades(res.data.data.grades);
          // Calculate total students from grades
          const totalStudents = Object.values(res.data.data.grades).reduce((sum, students) => sum + students.length, 0);
          // Calculate total classes (each grade is considered a class)
          const totalClasses = Object.keys(res.data.data.grades).length;
          setClasses(Object.entries(res.data.data.grades).map(([grade, students]) => ({
            _id: grade,
            name: `Grade ${grade}`,
            grade,
            students
          })));
        } else {
          setError(res.data.message);
        }
      } catch (err) {
        setError('Failed to fetch classes and students');
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };
    if (user && token) fetchClassesAndStudents();
  }, [user, token]);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  // Calculate stats from real data
  const stats = {
    totalClasses: classes.length,
    totalStudents: classes.reduce((sum, c) => sum + c.students.length, 0),
    activeAssignments: 0,
    pendingReviews: 0,
  };

  // Filter classes based on search
  const filteredClasses = classes.filter(c =>
    c.name.toLowerCase().includes(classSearch.toLowerCase()) ||
    c.grade.toLowerCase().includes(classSearch.toLowerCase())
  );

  // Filter students based on search
  const filteredStudents = selectedClass?.students.filter(s =>
    `${s.firstName} ${s.lastName}`.toLowerCase().includes(studentSearch.toLowerCase())
  ) || [];

  // Handlers
  const handleSelectClass = (classItem: Class) => {
    setSelectedClass(classItem);
    setView('classDetails');
  };
  const handleBackToDashboard = () => {
    setView('dashboard');
    setSelectedClass(null);
    setSelectedStudent(null);
    setStudentSearch('');
  };
  const handleSelectStudent = (student: Student) => {
    setSelectedStudent(student);
    setView('studentDetails');
  };
  const handleBackToClass = () => {
    setView('classDetails');
    setSelectedStudent(null);
  };
  // Export logic (mock)
  const handleExport = (type: 'classes' | 'students') => {
    alert(`Exporting ${type} as CSV (mock)`);
  };

  return (
    <DashboardContainer>
      <TeacherHeader />
      <DashboardMain>
        <DashboardContent>
          {view === 'dashboard' && (
            <>
              <DashboardHeader>
                <DashboardTitle>Teacher Dashboard</DashboardTitle>
                <ActionButtons>
                  <ActionButton onClick={() => setShowNotifications(s => !s)}>
                    <Bell size={20} />
                    {stats.pendingReviews > 0 && (
                      <span style={{position:'absolute',top:2,right:2,background:'#ef4444',color:'#fff',borderRadius:'50%',fontSize:10,padding:'2px 6px'}}>{stats.pendingReviews}</span>
                    )}
                  </ActionButton>
                  <ActionButton onClick={() => setShowProfile(true)}>
                    <Settings size={20} />
                  </ActionButton>
                </ActionButtons>
              </DashboardHeader>
              {/* Quick Actions */}
              <div style={{display:'flex',gap:16,marginBottom:24}}>
                <button style={{background:'#2563eb',color:'#fff',border:'none',borderRadius:6,padding:'0.5rem 1rem',fontWeight:500,cursor:'pointer'}} onClick={()=>alert('Create Assignment (mock)')}>Create Assignment</button>
                <button style={{background:'#059669',color:'#fff',border:'none',borderRadius:6,padding:'0.5rem 1rem',fontWeight:500,cursor:'pointer'}} onClick={()=>alert('Schedule Lesson (mock)')}>Schedule Lesson</button>
                <button style={{background:'#7c3aed',color:'#fff',border:'none',borderRadius:6,padding:'0.5rem 1rem',fontWeight:500,cursor:'pointer'}} onClick={()=>alert('Send Announcement (mock)')}>Send Announcement</button>
              </div>
              <WelcomeCard 
                teacherName={user?.name || 'Madam Elliot'}
                term={'Term 2'}
                alertCount={stats.pendingReviews}
              />
              <StatsGrid stats={stats} />
              <PerformanceAnalytics />
              <RecentActivities />
              <UpcomingEvents />
              {/* Messages Section */}
              <Section>
                <SectionTitle>
                  <span role="img" aria-label="messages">💬</span> Messages
                </SectionTitle>
                {user && (
                  <MessagesList 
                    user={{ 
                      id: user.id || '', 
                      name: user.name || 'Unknown User', 
                      role: 'teacher' 
                    }} 
                  />
                )}
              </Section>
              {/* Class search/filter and export */}
              <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:8}}>
                <input
                  type="text"
                  placeholder="Search classes..."
                  value={classSearch}
                  onChange={e => setClassSearch(e.target.value)}
                  style={{padding:'0.5rem',border:'1px solid #d1d5db',borderRadius:6,fontSize:'1em'}}
                />
                <button onClick={()=>handleExport('classes')} style={{background:'#f3f4f6',border:'none',borderRadius:6,padding:'0.5rem 1rem',cursor:'pointer'}}>Export Classes</button>
              </div>
              <ClassList 
                classes={filteredClasses}
                onSelectClass={handleSelectClass}
              />
              {showNotifications && (
                <div style={{background:'#fff',border:'1px solid #e5e7eb',borderRadius:8,padding:16,marginBottom:16}}>
                  <NotificationList notifications={[]} />
                  <button onClick={()=>setShowNotifications(false)} style={{marginTop:8,background:'#2563eb',color:'#fff',border:'none',borderRadius:6,padding:'0.5rem 1rem',cursor:'pointer'}}>Close</button>
                </div>
              )}
            </>
          )}
          {view === 'classDetails' && selectedClass && (
            <>
              {/* Student search/filter and export */}
              <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:8}}>
                <input
                  type="text"
                  placeholder="Search students..."
                  value={studentSearch}
                  onChange={e => setStudentSearch(e.target.value)}
                  style={{padding:'0.5rem',border:'1px solid #d1d5db',borderRadius:6,fontSize:'1em'}}
                />
                <button onClick={()=>handleExport('students')} style={{background:'#f3f4f6',border:'none',borderRadius:6,padding:'0.5rem 1rem',cursor:'pointer'}}>Export Students</button>
              </div>
              <ClassDetails
                classData={selectedClass}
                students={filteredStudents}
                subject={selectedClass.name}
                term={selectedClass.grade}
                onBack={handleBackToDashboard}
                onSelectStudent={handleSelectStudent}
              />
            </>
          )}
          {view === 'studentDetails' && selectedStudent && (
            <StudentDetails
              studentData={selectedStudent}
              onBack={handleBackToClass}
            />
          )}
          {showProfile && <TeacherProfile onClose={()=>setShowProfile(false)} />}
        </DashboardContent>
      </DashboardMain>
      <TeacherFooter />
      <HelpWidget />
    </DashboardContainer>
  );
};

export default Dashboard; 